import type { CrucibleClient } from '@volley/crucible-client';
import type { ProjectRef } from '@volley/crucible-ipc-types';

/** Add an immutable per-project cache partition without changing transport
 * authority. Methods stay bound to the underlying client so private transport
 * state is never copied or detached. */
export function scopeProjectQueries(client: CrucibleClient, project: ProjectRef): CrucibleClient {
  const queryScope = `project:${project.placement}:${project.gameId}`;
  return new Proxy(client, {
    get(target, property) {
      if (property === 'queryScope') return queryScope;
      const value = Reflect.get(target, property, target) as unknown;
      return typeof value === 'function' ? value.bind(target) : value;
    },
  });
}

/** Backwards-compatible Cloud call-site name. */
export const scopeCloudProjectQueries = scopeProjectQueries;

/** Build a React Query key that cannot alias a same-slug project in another
 * placement or a prior navigation authority generation. Dashboard callers
 * pass an explicit ProjectRef; workspace callers use the host-issued client
 * scope. Production workspace code fails closed when neither is available. */
export function projectQueryKey(
  client: Pick<CrucibleClient, 'queryScope'>,
  parts: readonly unknown[],
  project?: ProjectRef,
): readonly unknown[] {
  const scope =
    client.queryScope ?? (project ? `project:${project.placement}:${project.gameId}` : undefined);
  // Existing isolated hook tests intentionally provide a minimal unscoped
  // client. Keep their historical keys without weakening production, where
  // a missing host scope is always rejected.
  if (!scope && process.env.NODE_ENV === 'test') {
    return parts;
  }
  if (!scope) {
    throw new Error('A host-authorized project query scope is required.');
  }
  return ['project', scope, ...parts] as const;
}
